import { StaffPerformance, StaffMonthlyTrendStats } from '../types/SupportTypes';

export interface PerformanceMetrics {
  resolutionRateTrend: number;
  resolutionRateDifferenceToPreviousMonth: number;
  resolutionRatePreviousMonth: number;
  resolutionRateCurrentMonth: number;
  
  resolutionTimeTrend: number;
  resolutionTimePreviousMonth: number;
  resolutionTimeDifferenceToPreviousMonth: number;
  resolutionTimeCurrentMonth: number;
  
  firstResponseTimeTrend: number;
  firstResponseTimePreviousMonth: number;
  firstResponseTimeDifferenceToPreviousMonth: number;
  firstResponseTimeCurrentMonth: number;
  
  casesResolvedTrend: number;
  casesResolvedDifferenceToPreviousMonth: number;
  casesResolvedPreviousMonth: number;
  casesResolvedCurrentMonth: number;
  
  trendScore: number;
  overallScore: number;

  isNewStaff: boolean;
}

/**
 * Calculates performance metrics for a support team member
 * 
 * @param memberOrName The staff member object or name
 * @param monthlyTrendStats Monthly trend statistics for all staff members
 * @returns Performance metrics including trends and overall score
 */
export const calculateSupportMemberPerformance = (
  memberOrName: StaffPerformance | string,
  monthlyTrendStats: StaffMonthlyTrendStats
): PerformanceMetrics => {
  // Handle both StaffPerformance object and string name
  const memberName = typeof memberOrName === 'string' ? memberOrName : memberOrName.name;
  const memberData = monthlyTrendStats[memberName];

  const defaultMetrics: PerformanceMetrics = {
    resolutionRateTrend: 0,
    resolutionRateDifferenceToPreviousMonth: 0,
    resolutionRatePreviousMonth: 0,
    resolutionRateCurrentMonth: 0,
    
    resolutionTimeTrend: 0,
    resolutionTimePreviousMonth: 0,
    resolutionTimeDifferenceToPreviousMonth: 0,
    resolutionTimeCurrentMonth: 0,
    
    firstResponseTimeTrend: 0,
    firstResponseTimePreviousMonth: 0,
    firstResponseTimeDifferenceToPreviousMonth: 0,
    firstResponseTimeCurrentMonth: 0,
    
    casesResolvedTrend: 0,
    casesResolvedDifferenceToPreviousMonth: 0,
    casesResolvedPreviousMonth: 0,
    casesResolvedCurrentMonth: 0,    
    
    trendScore: 50,
    overallScore: 0,
    
    isNewStaff: false
  };

  if (!memberData) {
    return defaultMetrics;
  }

  const months = Object.keys(memberData).sort();
  if (months.length < 1) {
    return defaultMetrics;
  }

  const currentMonth = memberData[months[months.length - 1]];
  if (!currentMonth) {
    return defaultMetrics;
  }

  const { assignedCases = 0, resolvedCases = 0, avgResolutionTime, avgFirstResponseTime } = currentMonth;

  // Fair resolution rate: fallback to 100% if resolved > 0 but none were assigned
  const currentResolutionRate = assignedCases > 0 ? (resolvedCases / assignedCases) * 100 : resolvedCases > 0 ? 100 : 0;
  const resolutionRateCurrentMonth = currentResolutionRate;
  // Productivity score: scale of 0–100 based on resolution volume. should calculate the percentage of cases this person resolved out of the total number of resolved cases for the current month
  const currentMonthName = months[months.length - 1];
  let isNewStaff = true;
  // Sum up all resolved cases for the current month across all staff members
  const totalResolvedCases = Object.keys(monthlyTrendStats).reduce((acc, staffName) => {
    const staffData = monthlyTrendStats[staffName];
    const staffCurrentMonthData = staffData[currentMonthName];
    return acc + (staffCurrentMonthData?.resolvedCases || 0);
  }, 0);
  
  const resolutionProductivityScore = totalResolvedCases > 0 ? (resolvedCases / totalResolvedCases) * 100 : 0;
  
  
  const casesResolvedCurrentMonth = currentMonth.resolvedCases || 0;

  // If only one month, use productivity directly
  if (months.length < 2) {
    const overallScore = (currentResolutionRate * 0.5) + (resolutionProductivityScore * 0.5);
    return {
      ...defaultMetrics,
      casesResolvedCurrentMonth,
      resolutionRateCurrentMonth,
      trendScore: 50,
      overallScore,
      isNewStaff
    };
  }

  const previousMonth = memberData[months[months.length - 2]];
  if (!previousMonth) {
    const overallScore = (currentResolutionRate * 0.5) + (resolutionProductivityScore * 0.5);
    return {
      ...defaultMetrics,
      casesResolvedCurrentMonth,
      resolutionRateCurrentMonth,
      trendScore: 50,
      overallScore,
      isNewStaff
    };
  }

  isNewStaff = false;

  // Trends
  const previousResolutionRate =
    previousMonth.assignedCases && previousMonth.assignedCases > 0
      ? (previousMonth.resolvedCases! / previousMonth.assignedCases) * 100
      : previousMonth.resolvedCases! > 0
        ? 100
        : 0;

  //all times are in minutes

  

  const resolutionRatePreviousMonth = previousResolutionRate;
  //current resolution month defined above
  const resolutionRateDifferenceToPreviousMonth = currentResolutionRate - previousResolutionRate;
  const resolutionRateTrend = previousResolutionRate > 0 ? (resolutionRateDifferenceToPreviousMonth / previousResolutionRate) * 100 : resolutionRateDifferenceToPreviousMonth > 0 ? 100 : 0;    
    
  const resolutionTimePreviousMonth = previousMonth.avgResolutionTime;
  const resolutionTimeCurrentMonth = currentMonth.avgResolutionTime;
  const resolutionTimeDifferenceToPreviousMonth = currentMonth.avgResolutionTime - previousMonth.avgResolutionTime;  
  const resolutionTimeTrend = previousMonth.avgResolutionTime > 0 ? (resolutionTimeDifferenceToPreviousMonth / previousMonth.avgResolutionTime) * 100 : resolutionTimeDifferenceToPreviousMonth > 0 ? 100 : 0;
  
  const firstResponseTimePreviousMonth = previousMonth.avgFirstResponseTime;
  const firstResponseTimeCurrentMonth = currentMonth.avgFirstResponseTime;
  const firstResponseTimeDifferenceToPreviousMonth = currentMonth.avgFirstResponseTime - previousMonth.avgFirstResponseTime;
  const firstResponseTimeTrend = previousMonth.avgFirstResponseTime > 0 ? (firstResponseTimeDifferenceToPreviousMonth / previousMonth.avgFirstResponseTime) * 100 : firstResponseTimeDifferenceToPreviousMonth > 0 ? 100 : 0;
  
  const casesResolvedPreviousMonth = previousMonth.resolvedCases || 0;
  //current month defined above
  const casesResolvedDifferenceToPreviousMonth = (currentMonth.resolvedCases || 0) -(previousMonth.resolvedCases || 0);
  const casesResolvedTrend = (previousMonth.resolvedCases || 0) > 0 ? (casesResolvedDifferenceToPreviousMonth / (previousMonth.resolvedCases || 0)) * 100 : casesResolvedDifferenceToPreviousMonth > 0 ? 100 : 0;
  
  
  

  // Normalized trend score (0–100)
  // For resolution time and first response time, negative trends are good (less time)
  // For cases resolved, positive trends are good (more cases)
  // For resolution rate, positive trends are good (higher rate)
  const trendScore =
    // Resolution rate: convert difference to 0-100 scale (50 is neutral)
    (Math.min(Math.max(resolutionRateTrend + 50, 0), 100) * 0.4) +
    // Resolution time: invert percentage so negative is good
    (Math.min(Math.max(50 - resolutionTimeTrend, 0), 100) * 0.3) +
    // First response time: invert percentage so negative is good
    (Math.min(Math.max(50 - firstResponseTimeTrend, 0), 100) * 0.2) +
    // Cases resolved: positive percentage is good
    (Math.min(Math.max(casesResolvedTrend + 50, 0), 100) * 0.1);

  // Final weighted score
  const overallScore =
    (currentResolutionRate * 0.4) +
    (resolutionProductivityScore * 0.3) +
    (trendScore * 0.3);

  return {
    resolutionRateTrend,    
    resolutionRateDifferenceToPreviousMonth,
    resolutionRatePreviousMonth,
    resolutionRateCurrentMonth,
    
    resolutionTimeTrend,
    resolutionTimePreviousMonth,
    resolutionTimeCurrentMonth,
    resolutionTimeDifferenceToPreviousMonth,
    
    firstResponseTimeTrend,
    firstResponseTimePreviousMonth,
    firstResponseTimeCurrentMonth,
    firstResponseTimeDifferenceToPreviousMonth,
    
    casesResolvedTrend,
    casesResolvedDifferenceToPreviousMonth,
    casesResolvedPreviousMonth,
    casesResolvedCurrentMonth,    
    
    trendScore,
    overallScore,
    isNewStaff
  };
};


/**
 * Sorts support team members by their performance score in descending order
 * 
 * @param members Array of staff performance data (optional)
 * @param monthlyTrendStats Monthly trend statistics for all staff members
 * @returns Sorted array of staff members with performance scores
 */
export const sortMembersByPerformance = (
  membersOrStats: StaffPerformance[] | StaffMonthlyTrendStats,
  monthlyTrendStats?: StaffMonthlyTrendStats,
  fullSnapshotStaffPerformance?: StaffPerformance[]
): (StaffPerformance & { overallScore: number })[] => {
  // Handle both legacy and new usage patterns
  if (Array.isArray(membersOrStats) && monthlyTrendStats) {
    // Legacy usage: sortMembersByPerformance(members, monthlyTrendStats)
    const members = membersOrStats;
    return members
      .map(member => {
        const { overallScore } = calculateSupportMemberPerformance(member, monthlyTrendStats);
        return { ...member, overallScore };
      })
      .sort((a, b) => b.overallScore - a.overallScore);
  } else {
    // New usage: sortMembersByPerformance(monthlyTrendStats)
    const stats = membersOrStats as StaffMonthlyTrendStats;
    const memberNames = Object.keys(stats);
    
    return memberNames
      .map(memberName => {
        const { overallScore } = calculateSupportMemberPerformance(memberName, stats);
        
        //get staff performance record from snapshot
        let snapshotPerformance = fullSnapshotStaffPerformance?.find(p => p.name === memberName);
        if (!snapshotPerformance) {          
          // Create a minimal StaffPerformance object with the name and add overallScore
          snapshotPerformance = {
            name: memberName,
            assignedCases: 0,
            resolvedCases: 0,
            avgResolutionTime: 0,
            avgFirstResponseTime: 0          
          };
        }
        return { ...snapshotPerformance, overallScore };
      })
      .sort((a, b) => b.overallScore - a.overallScore);
  }
};
